/*!
 * chord-voice.js — sound a chord, so a chart can be heard and not only read.
 *
 * Browser global `window.ChordVoice`. Web Audio only, no samples, no fetch.
 *
 * ── WHY A TOOL THAT PRINTS CHORDS NEEDS THIS ─────────────────────────────
 * /chord-finder answers "which chords are in this recording", so clicking a
 * chord there can simply seek the recording — the chord is in the file. A tool
 * that SUGGESTS chords for a melody has nothing to seek to: the chords exist
 * only on the screen. Without a voice, «Am» is a word and a grid of dots, and
 * the one question the person came with — does this sound right under what I
 * sang — cannot be answered at all.
 *
 * ── WHY SYNTHESIS AND NOT A SOUNDFONT ────────────────────────────────────
 * public/vendor/midi-soundfonts exists, and it is megabytes per instrument
 * fetched before the first note. This is an audition, not a performance: what
 * it has to carry is which notes are in the chord and whether the chord fits,
 * and a plucked additive voice carries both at a cost of zero bytes and zero
 * latency. A phone on a slow connection hears the chord immediately.
 *
 * The tone is deliberately plain: three partials with a plucked envelope, the
 * root doubled an octave below so the chord has a bottom, and a slight strum
 * so the notes do not arrive as one block. A brighter or more "produced" sound
 * would compete with the voice it is supposed to sit under.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ChordVoice = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var A4 = 440, A4_MIDI = 69;
  function hzOf(midi) { return A4 * Math.pow(2, (midi - A4_MIDI) / 12); }

  /* Voicing: the root low, the rest gathered around the middle of the keyboard.
   * Spreading a triad across two octaves is what makes it read as a CHORD
   * rather than as three separate notes, and keeping the upper voices under
   * ~C5 keeps them out of the way of a sung melody, which lives right there. */
  function voice(root, intervals, opts) {
    opts = opts || {};
    var base = opts.base === undefined ? 48 : opts.base;   // C3
    var out = [root + base - 12];                          // bass
    for (var i = 0; i < intervals.length; i++) {
      var n = base + root + intervals[i];
      while (n < base + 4) n += 12;
      while (n > base + 20) n -= 12;
      out.push(n);
    }
    return out;
  }

  function create(ctx, opts) {
    opts = opts || {};
    var out = ctx.createGain();
    out.gain.value = opts.gain === undefined ? 0.5 : opts.gain;
    out.connect(opts.destination || ctx.destination);
    var live = [];

    /* One plucked note: three partials, exponential decay. `when` is on the
     * context clock, so a whole progression can be scheduled ahead and stay in
     * time with an <audio> element playing the take. */
    function note(midi, when, dur, gain) {
      var f = hzOf(midi);
      var g = ctx.createGain();
      g.gain.setValueAtTime(0, when);
      g.gain.linearRampToValueAtTime(gain, when + 0.012);
      /* A long tail sounds like a piano; a short one sounds like a click. The
       * decay is tied to the chord's length so a chord held for two bars does
       * not go silent halfway through it. */
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * 0.06), when + Math.max(0.3, dur));
      g.gain.linearRampToValueAtTime(0, when + Math.max(0.32, dur) + 0.04);
      g.connect(out);
      var partials = [[1, 1], [2, 0.32], [3, 0.14]];
      for (var p = 0; p < partials.length; p++) {
        var o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = f * partials[p][0];
        var pg = ctx.createGain();
        pg.gain.value = partials[p][1];
        o.connect(pg); pg.connect(g);
        o.start(when);
        o.stop(when + Math.max(0.36, dur) + 0.08);
        live.push(o);
        o.onended = (function (osc) {
          return function () { var i = live.indexOf(osc); if (i >= 0) live.splice(i, 1); };
        })(o);
      }
    }

    return {
      /**
       * @param {number} root      pitch class 0..11
       * @param {number[]} ivals   semitones of the chord above the root
       * @param {number} when      context time
       * @param {number} dur       seconds the chord is held
       */
      play: function (root, ivals, when, dur, gain) {
        if (root === null || root === undefined || !ivals || !ivals.length) return;
        var notes = voice(root, ivals, opts);
        var g = (gain === undefined ? 0.18 : gain);
        for (var i = 0; i < notes.length; i++) {
          // a 12 ms strum, low to high, so the chord is not one block of sound
          note(notes[i], when + i * 0.012, dur, i === 0 ? g * 0.9 : g);
        }
      },
      /** Silence everything now — a seek, a stop, a new take. */
      stop: function () {
        for (var i = live.length - 1; i >= 0; i--) {
          try { live[i].stop(); } catch (e) {}
        }
        live.length = 0;
      },
      gain: out,
      dispose: function () {
        this.stop();
        try { out.disconnect(); } catch (e) {}
      }
    };
  }

  return { create: create, voice: voice, hzOf: hzOf };
});
